<template>
  <div
    class="timeline-item"
    :style="`left: ${startPosition}%; width: ${timelineWidth}%; background-color: ${vehicle.color}`"
  ></div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import moment from 'moment'

export default {
  mixins: [Helper],
  props: {
    vehicle: {
      type: Object,
      default: () => {},
    },
    expectedStartTime: {
      type: String,
    },
    totalMinutes: {
      type: Number,
    },
  },
  data() {
    return {
      startPosition: 0,
      timelineWidth: 0,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureStrategies: 'identity/getFeatureStrategies',
    }),
    isRoSwatActive() {
      const isSamator =
        this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.is_samator || false
      const isActive =
        this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.active || false

      return isSamator && isActive
    },
    timelines() {
      return this.vehicle?.timelines || []
    },
  },
  watch: {
    expectedStartTime: 'calculateTimelineStyle',
    totalMinutes: 'calculateTimelineStyle',
    timelines: {
      handler: 'calculateTimelineStyle',
      deep: true,
    },
  },
  mounted() {
    this.calculateTimelineStyle()
  },
  methods: {
    getPointMoment(point) {
      if (!point) return null

      const processTime = moment(point.estimated_process_on)
      if (processTime.isValid()) return processTime

      const departureTime = moment(point.estimated_departure_on)
      if (departureTime.isValid()) return departureTime

      return null
    },
    isRenderableTimelinePoint(point) {
      if (!point) return false
      if (point.type === 'BREAK' || point.type === 'IDLE') return true

      return point.type === 'DEFAULT' && point.service_time !== 'PT0S'
    },
    getStartPickupDefaultPoint() {
      if (!this.isRoSwatActive) return null

      const startPoint = this.timelines.find((point) => point.type === 'START')
      const pickupDefaultPoint = this.timelines.find((point) => {
        if (point.type !== 'DEFAULT') return false

        const hasPickupActivity = point.activities?.some((activity) => activity.type === 'PICKUP')
        if (!hasPickupActivity) return false

        const startAddressId = startPoint?.address?.id
        const pointAddressId = point.address?.id

        if (startAddressId != null && pointAddressId != null) {
          return startAddressId === pointAddressId
        }

        return startPoint?.address?.name === point.address?.name
      })

      return pickupDefaultPoint || null
    },
    getTimelineStartPoint() {
      const startPoint = this.timelines.find((point) => point.type === 'START')
      const hasValidStartPoint = this.getPointMoment(startPoint)

      if (!this.isRoSwatActive && hasValidStartPoint) return startPoint

      if (this.isRoSwatActive) {
        const startPickupPoint = this.getStartPickupDefaultPoint()
        if (this.getPointMoment(startPickupPoint)) return startPickupPoint
      }

      if (hasValidStartPoint) return startPoint

      const firstRenderablePoint = this.timelines.find((point) =>
        this.isRenderableTimelinePoint(point)
      )
      if (firstRenderablePoint) return firstRenderablePoint

      return this.timelines.find((point) => point.type !== 'START') || this.timelines[0] || null
    },
    async calculateTimelineStyle() {
      const hasValidTotalMinutes =
        typeof this.totalMinutes === 'number' &&
        this.totalMinutes > 0 &&
        Number.isFinite(this.totalMinutes)
      if (!hasValidTotalMinutes || !this.expectedStartTime || this.timelines.length === 0) {
        this.startPosition = 0
        this.timelineWidth = 0
        return
      }

      const timelineStartPoint = this.getTimelineStartPoint()
      if (!timelineStartPoint) {
        this.startPosition = 0
        this.timelineWidth = 0
        return
      }

      const startTime = this.getPointMoment(timelineStartPoint)
      const startCol = moment(this.expectedStartTime).minutes(0)
      if (!startTime || !startCol.isValid()) {
        this.startPosition = 0
        this.timelineWidth = 0
        return
      }

      await this.$nextTick()
      this.startPosition = (startTime.diff(startCol, 'minutes') / this.totalMinutes) * 100

      const lastTimelineIdx = this.timelines.length - 1
      const lastTimeline = this.timelines[lastTimelineIdx]
      if (!lastTimeline) {
        this.timelineWidth = 0
        return
      }

      // Use estimated_process_on if type is 'end', otherwise use estimated_departure_on
      const endTime =
        lastTimeline.type === 'END'
          ? moment(lastTimeline.estimated_process_on)
          : moment(lastTimeline.estimated_departure_on)
      if (!endTime.isValid()) {
        this.timelineWidth = 0
        return
      }

      const diffMinutes = endTime.diff(startTime, 'minutes')
      this.timelineWidth = Math.max((diffMinutes / this.totalMinutes) * 100, 0)
    },
  },
}
</script>

<style lang="scss" scoped>
.timeline-item {
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);

  height: 9px;
  border-radius: 5px;
  z-index: 1;
}
</style>
